import { useRuntimeConfig } from '#app'
import { defineNuxtPlugin } from '#imports'

const MAX_RETRIES = 3
const RETRY_DELAY = 1000

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const retry = async (fn, retries = MAX_RETRIES, delay = RETRY_DELAY) => {
  let lastError
  for (let i = 0; i < retries; i++) {
    try {
      return await fn()
    } catch (e) {
      lastError = e
      if (i < retries - 1) {
        await sleep(delay * (i + 1))
      }
    }
  }
  throw lastError
}

export default defineNuxtPlugin((nuxtApp) => {
  const { bucketName, clientId } = useRuntimeConfig().public
  let endpoints = []
  let endpointsPromise = null
  let visibilityHandler = null

  // 不用 useScript + CDN：esm.min.js / iife.min.js 只有 latest（實測版號路徑不是選擇器，亂填版號照樣回同一份），
  // bundle: true 由建置期下載等於 agent 版本會無 commit 自行變動，出事時無法回溯是哪一版。
  // 改為釘版 app/vendor/fingerprint/v3.11.12.js，升級時手動跑 `npm run fingerprint:fetch` 並把 bytes commit 進 repo。

  const clearCache = () => {
    endpoints = []
    endpointsPromise = null
  }

  // 頁面隱藏時清除快取，回來時重新獲取最新資料
  if (typeof document !== 'undefined') {
    visibilityHandler = () => {
      if (document.visibilityState === 'hidden') {
        clearCache()
      }
    }
    document.addEventListener('visibilitychange', visibilityHandler)
  }

  // 清理 event listener，避免記憶體洩漏
  nuxtApp.hooks.hook('app:unmount', () => {
    if (visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', visibilityHandler)
      visibilityHandler = null
    }
    clearCache()
  })

  const fetchEndpoints = async () => {
    if (endpointsPromise) {
      return endpointsPromise
    }

    if (!bucketName || !clientId) {
      endpoints = []
      return
    }

    endpointsPromise = (async () => {
      try {
        const url = `https://storage.googleapis.com/${bucketName}/site_resources/${clientId}/target/target.json`
        const response = await fetch(url)
        const { host: hosts } = await response.json()
        endpoints = [...new Set(hosts.map((host) => host.replace(/fnapi\d*/, 'fp')))]
      } catch (e) {
        console.error('Failed to fetch fingerprint endpoints:', e)
        endpoints = []
        endpointsPromise = null
      }
    })()

    return endpointsPromise
  }

  const getDeviceToken = async () => {
    try {
      await fetchEndpoints()
      // 動態 import 維持原本 useScript trigger: 'manual' 的延遲載入行為
      const { load, defaultEndpoint } = await import('@/vendor/fingerprint/v3.11.12.js')

      const fp = await retry(() =>
        load({
          endpoint: [...endpoints, defaultEndpoint],
        })
      )

      const result = await retry(() => fp.get({ timeout: 30000 }))
      return result.visitorId
    } catch (e) {
      console.error('Failed to get device token:', e)
      return null
    }
  }

  return {
    provide: {
      getDeviceToken,
    },
  }
})
